// always reflect changes made here in the functions/config/integrations.js file on the backend side

const { INTEGRATION_NAMES } = require('../constants/integrations');

/**
 * @typedef {'aloha' | 'amrex' | 'vireo' | 'lumary' | 'boost' | 'camber'} IntegrationName
 */

const INTEGRATION_FEATURES = {
  SEND_STAFF_ACTIVITIES_WITHOUT_LINKED_APPOINTMENTS:
    'sendStaffActivitiesWithoutLinkedAppointments',
  SEND_CANCELLATION_REQUESTS: 'sendCancellationRequests',
  CREATE_APPOINTMENT_ADJUSTMENT_ADDENDUMS: 'createAppointmentAdjustmentAddendums',
};

const DEFAULT_INTEGRATION_FEATURES = {
  [INTEGRATION_FEATURES.SEND_STAFF_ACTIVITIES_WITHOUT_LINKED_APPOINTMENTS]: false,
  [INTEGRATION_FEATURES.SEND_CANCELLATION_REQUESTS]: false,
  [INTEGRATION_FEATURES.CREATE_APPOINTMENT_ADJUSTMENT_ADDENDUMS]: false,
};

const INTEGRATION_CONFIG = {
  [INTEGRATION_NAMES.CAMBER]: {
    features: {
      [INTEGRATION_FEATURES.SEND_STAFF_ACTIVITIES_WITHOUT_LINKED_APPOINTMENTS]: true,
      [INTEGRATION_FEATURES.SEND_CANCELLATION_REQUESTS]: true,
      [INTEGRATION_FEATURES.CREATE_APPOINTMENT_ADJUSTMENT_ADDENDUMS]: true,
    },
  },
};

/**
 * Returns feature flags configured for the given integration.
 * Integration-specific feature flags override default values.
 *
 * @param {IntegrationName} integrationName - The integration name.
 * @returns {Record<string, boolean>} A merged object containing feature flags for the integration.
 */
const getIntegrationFeatures = (integrationName) => {
  return {
    ...DEFAULT_INTEGRATION_FEATURES,
    ...(INTEGRATION_CONFIG[integrationName]?.features || {}),
  };
};

/**
 * Checks whether a specific feature flag is enabled for the given integration.
 *
 * @param {IntegrationName} integrationName - The integration name.
 * @param {string} featureName - The feature flag name to check.
 * @returns {boolean} Returns true if the feature is enabled; otherwise false.
 */
const isIntegrationFeatureEnabled = (integrationName, featureName) => {
  if (!integrationName) {
    return false;
  }

  return Boolean(getIntegrationFeatures(integrationName)[featureName]);
};

/**
 * Checks whether the integration allows sending appointments cancellation requests.
 *
 * @param {IntegrationName} integrationName - The integration name.
 * @returns {boolean} Returns true if appointments cancellation requests can be sent; otherwise false.
 */
const canSendCancellationRequests = (integrationName) => {
  return isIntegrationFeatureEnabled(
    integrationName,
    INTEGRATION_FEATURES.SEND_CANCELLATION_REQUESTS,
  );
};

/**
 * Checks whether the integration allows sending staff activities without linked appointments.
 *
 * @param {IntegrationName} integrationName - The integration name.
 * @returns {boolean} Returns true if staff activities without linked appointments can be sent; otherwise false.
 */
const canSendStaffActivitiesWithoutLinkedAppointments = (integrationName) => {
  return isIntegrationFeatureEnabled(
    integrationName,
    INTEGRATION_FEATURES.SEND_STAFF_ACTIVITIES_WITHOUT_LINKED_APPOINTMENTS,
  );
};

/**
 * Checks whether the integration creates addendums on signed notes when a completed
 * appointment is adjusted.
 *
 * @param {IntegrationName} integrationName - The integration name.
 * @returns {boolean} Returns true if appointment adjustments create addendums; otherwise false.
 */
const canCreateAppointmentAdjustmentAddendums = (integrationName) => {
  return isIntegrationFeatureEnabled(
    integrationName,
    INTEGRATION_FEATURES.CREATE_APPOINTMENT_ADJUSTMENT_ADDENDUMS,
  );
};

module.exports = {
  INTEGRATION_CONFIG,
  INTEGRATION_FEATURES,
  getIntegrationFeatures,
  isIntegrationFeatureEnabled,
  canSendCancellationRequests,
  canSendStaffActivitiesWithoutLinkedAppointments,
  canCreateAppointmentAdjustmentAddendums,
};
